import React, { useEffect, useRef, useState } from 'react'
import { message, Upload, Image } from 'antd'
import type { UploadFile, UploadProps as AntdUploadProps } from 'antd/es/upload/interface'
// import icon from './upload-icon.png'
import styles from './index.module.scss'
import { $t } from '@/i18n/intl'
import { LoadingOutlined, CloseOutlined } from '@ant-design/icons'
import ImgCrop, { ImgCropProps } from 'antd-img-crop'
import { uploadFile } from '@/business/cos/public'
import { calculateFileMd5 } from '@/business/cos/util'
import cn from 'classnames'

const { Dragger } = Upload

const switchHttps = (url: string) => {
  return url.replace('http:', 'https:')
}

export interface UploadProps extends Omit<AntdUploadProps, 'onChange'> {
  value?: string | string[]
  placeholder?: string
  description?: string
  onChange?: (val: any) => void
  upLoadRequestFn?: (file: any) => Promise<{
    status: boolean
    url: string
    msg?: string
  }>
  limit?: number //单位 MB
  deleteImage?: (value: string) => void
  useDraggerUpload?: boolean
  useImgCrop?: boolean
  imgCropProps?: ImgCropProps
  className?: string
  showDeleteBtn?: boolean
  previewBoxClassName?: string
  multiple?: boolean
  maxCount?: number
  showSuccessMessage?: boolean
}

const normalizeValueList = (val?: string | string[]) => {
  if (Array.isArray(val)) {
    return val.filter(Boolean)
  }
  return val ? [val] : []
}

const getFileSuffix = (file: any) => {
  const fileName = typeof file?.name === 'string' ? file.name : ''
  const match = fileName.match(/\.([^.\\/]+)$/)
  return match ? `.${match[1].toLowerCase()}` : ''
}

const InnerUpload: React.FC<UploadProps> = props => {
  const [loading, setLoading] = useState(false)
  const [value, setValue] = useState<string>(Array.isArray(props?.value) ? '' : props?.value || '')
  const [valueList, setValueList] = useState<string[]>(normalizeValueList(props?.value))
  const pendingUploadCountRef = useRef(0)
  const uploadRequestCountRef = useRef(0)
  const {
    placeholder = '点击上传图片',
    description,
    //  $t('upload.placeholder'),
    limit = 10,
    upLoadRequestFn,
    useDraggerUpload = true,
    useImgCrop = false,
    children = null,
    imgCropProps = {},
    showDeleteBtn = true,
    multiple = false,
    maxCount = 9,
    showSuccessMessage = true
  } = props

  useEffect(() => {
    setValue(Array.isArray(props.value) ? '' : props.value || '')
    setValueList(normalizeValueList(props.value))
  }, [props.value])

  const beforeUpload = (file: UploadFile) => {
    const isJpgOrPng =
      file.type === 'image/jpeg' ||
      file.type === 'image/png' ||
      file.type === 'image/jpg' ||
      file.type === 'image/webp'
    if (!isJpgOrPng) {
      message.error(
        $t('upload.file_type_limit.tips', { values: { str: 'JPG/PNG/WEBP' } })
        //  '图片格式必须是JPG/PNG/WEBP类型!'
      )
    }
    const overload = file?.size && file.size / 1024 / 1024 < limit
    if (!overload) {
      message.error(
        $t('upload.limit.size.tps', {
          values: {
            size: limit
          }
        })
        // `图片必须小于${limit}MB,请压缩后在上传`
      )
    }
    if (!isJpgOrPng || !overload) {
      return Upload.LIST_IGNORE
    }

    if (multiple) {
      const currentCount = valueList.length + pendingUploadCountRef.current
      if (currentCount >= maxCount) {
        message.warning(`最多上传 ${maxCount} 张商品图片`)
        return Upload.LIST_IGNORE
      }
      pendingUploadCountRef.current += 1
    }

    return true
  }

  const customRequest = async (config: any) => {
    const formData = new FormData()
    formData.append('file', config.file)
    uploadRequestCountRef.current += 1
    setLoading(true)
    try {
      let url = ''
      if (typeof upLoadRequestFn === 'function') {
        const res = await upLoadRequestFn?.(config.file) //返回必须是一个图片地址
        if (res.status) {
          url = res.url
        } else {
          config.file.status = 'done'
          config?.onError($t('upload.upload_failed'))
        }
      }
      // fileKeyRef.current = key
      if (url) {
        if (showSuccessMessage) {
          message.success($t('upload.upload_success'))
        }
        const _url = switchHttps(url)
        config.url = _url
        if (multiple) {
          setValueList(prev => {
            const next = [...prev, _url].slice(0, maxCount)
            onChange(next)
            return next
          })
        } else {
          setValue(_url)
          onChange(_url)
        }
        config?.onSuccess(_url)
        config.file.status = 'done'
      } else {
        config?.onError($t('upload.upload_failed'))
      }
    } catch (err) {
      config?.onError($t('upload.upload_failed'))
      console.error('err', err)
    } finally {
      uploadRequestCountRef.current = Math.max(0, uploadRequestCountRef.current - 1)
      if (multiple) {
        pendingUploadCountRef.current = Math.max(0, pendingUploadCountRef.current - 1)
      }
      if (uploadRequestCountRef.current === 0) {
        setLoading(false)
      }
    }
  }

  const uploadProps: any = {
    name: 'file',
    showUploadList: false,
    customRequest: customRequest,
    beforeUpload,
    disabled: props.disabled,
    multiple,
    className: cn(props.className)
  }

  const onChange = (v: string | string[]) => {
    if (props.onChange) {
      props.onChange(v)
    }
  }

  const del = () => {
    // const key = getKeyByUrl(value)
    if (props.disabled) {
      return
    }
    props?.deleteImage?.(value)
    if (value) {
      setValue('')
      onChange('')
    }
  }

  const delItem = (index: number) => {
    if (props.disabled) {
      return
    }
    setValueList(prev => {
      const target = prev[index]
      if (target) {
        props?.deleteImage?.(target)
      }
      const next = prev.filter((_, i) => i !== index)
      onChange(next)
      return next
    })
  }

  const uploadPlaceholderNode = children || (
    <div className={styles.placeholderWrap}>
      {loading ? (
        <div className={styles.loadingWrap}>
          <LoadingOutlined />
        </div>
      ) : (
        <div className={styles.box}>
          {/* <img className={styles.icon} src={icon} alt="" /> */}
          <p className={styles.text}>{placeholder}</p>
          {description ? <p className={styles.description}>{description}</p> : null}
        </div>
      )}
    </div>
  )

  const addUploadPlaceholderNode = (
    <div className={styles.addPlaceholderWrap}>
      {loading ? (
        <div className={styles.loadingWrap}>
          <LoadingOutlined />
        </div>
      ) : (
        <>
          <span className={styles.addIcon}>+</span>
          <span className={styles.addText}>添加</span>
        </>
      )}
    </div>
  )

  const renderUploadNode = (node: React.ReactNode) =>
    useDraggerUpload ? (
      <Dragger {...uploadProps}>{node}</Dragger>
    ) : (
      <Upload {...uploadProps}>{node}</Upload>
    )

  const uploadNode = renderUploadNode(uploadPlaceholderNode)
  const addUploadNode = renderUploadNode(addUploadPlaceholderNode)

  if (children) {
    return useImgCrop ? (
      <ImgCrop showGrid showReset {...imgCropProps}>
        {uploadNode}
      </ImgCrop>
    ) : (
      uploadNode
    )
  }

  if (multiple && !children) {
    const canUploadMore = valueList.length < maxCount

    return (
      <div
        className={cn(
          styles.uploadContainer,
          styles.multiUploadContainer,
          props.previewBoxClassName,
          {
            [styles.multiUploadContainerEmpty]: valueList.length === 0,
            [styles.uploadDisabled]: props.disabled
          }
        )}
      >
        {valueList.length > 0 ? (
          <>
            <div className={styles.multiUploadList}>
              {valueList.map((url, index) => (
                <div key={`${url}-${index}`} className={styles.multiPreviewBox}>
                  <Image src={url} />
                  {showDeleteBtn && (
                    <button
                      type="button"
                      className={styles.deleteBtn}
                      onClick={() => delItem(index)}
                      aria-label="删除图片"
                    >
                      <CloseOutlined />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {canUploadMore ? <div className={styles.multiUploadItem}>{addUploadNode}</div> : null}
          </>
        ) : (
          <div className={styles.multiUploadItem}>{uploadNode}</div>
        )}
      </div>
    )
  }

  if (value && !children) {
    return (
      <div
        className={cn(styles.uploadContainer, props.previewBoxClassName, {
          [styles.uploadDisabled]: props.disabled
        })}
      >
        <div className={styles.previewBox}>
          <Image src={value} />
          {showDeleteBtn && (
            <button type="button" className={styles.deleteBtn} onClick={del} aria-label="删除图片">
              <CloseOutlined />
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(styles.uploadContainer, {
        [styles.uploadDisabled]: props.disabled
      })}
    >
      {useImgCrop ? (
        <ImgCrop
          showGrid
          // aspectSlider
          showReset
          {...imgCropProps}
        >
          {uploadNode}
        </ImgCrop>
      ) : (
        uploadNode
      )}
    </div>
  )
}

export const CosUpload = (props: UploadProps) => {
  const deleteImage = (value: string) => {
    console.warn(value)
    props.deleteImage?.(value)
  }

  const upLoadRequestFn = async (file: any) => {
    try {
      const md5 = await calculateFileMd5(file)
      const key = `${md5}${getFileSuffix(file)}`
      const res = await uploadFile(file, key)

      if (res?.url) {
        return {
          status: true,
          url: res.url,
          msg: ''
        }
      }
      return {
        status: false,
        url: '',
        msg: ''
      }
    } catch (err) {
      return {
        status: true,
        url: '',
        msg: ''
      }
    }
  }

  return (
    <InnerUpload
      {...props}
      className={cn(styles.uploadBox, props.className)}
      deleteImage={deleteImage}
      upLoadRequestFn={upLoadRequestFn}
    />
  )
}
